<template>
  <div class="profile-page">
    <div class="page-header">
      <h2 class="page-title">个人中心</h2>
      <p class="page-desc">管理个人信息与账户设置</p>
    </div>

    <div class="profile-container">
      <el-card class="profile-card" shadow="hover">
        <template #header>
          <div class="card-header-title">
            <el-icon><User /></el-icon>
            <span>基本信息</span>
          </div>
        </template>

        <el-form
          :model="form"
          :rules="formRules"
          ref="formRef"
          label-width="110px"
          class="profile-form"
        >
          <el-form-item label="登录账号" prop="account">
            <el-input v-model="form.account" disabled />
            <div class="form-tip">账号固定，不可修改</div>
          </el-form-item>

          <el-form-item label="商户昵称" prop="nickName">
            <el-input v-model="form.nickName" placeholder="请输入商户昵称" />
          </el-form-item>

          <el-form-item label="联系手机" prop="mobile">
            <el-input v-model="form.mobile" placeholder="请输入手机号" />
          </el-form-item>

          <el-form-item label="当前点数">
            <el-tag type="success" size="default">{{ form.dianshu }} 点</el-tag>
          </el-form-item>

          <el-form-item label="账号状态">
            <el-tag :type="form.is_disable === 0 ? 'success' : 'danger'">
              {{ form.is_disable === 0 ? '正常启用' : '已禁用' }}
            </el-tag>
          </el-form-item>

          <el-form-item label="创建时间">
            <span>{{ formatCreateTime(form.create_time) }}</span>
          </el-form-item>

          <el-form-item class="save-btn-item">
            <el-button type="primary" @click="saveBaseInfo" :loading="submitting" size="default" class="save-btn">
              <el-icon><Check /></el-icon>保存基本信息
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { User, Check } from '@element-plus/icons-vue'

export default {
  name: 'ProfilePage',
  components: { User, Check },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const storeId = ref(userInfo.id || 0)
    const agentId = ref(userInfo.agent_id || 0)

    const formRef = ref(null)
    const submitting = ref(false)

    const form = reactive({
      id: storeId.value,
      account: userInfo.account || '',
      nickName: userInfo.nickName || '',
      mobile: userInfo.mobile || '',
      dianshu: userInfo.dianshu || '0.00',
      is_disable: userInfo.is_disable || 0,
      create_time: userInfo.create_time || 0,
    })

    const formRules = {
      nickName: [{ required: true, message: '请输入商户昵称', trigger: 'blur' }],
      mobile: [
        { required: true, message: '请输入手机号', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
      ]
    }

    const fetchProfile = async () => {
      const loading = ElLoading.service({ text: '加载中...' })
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/profile_api.php`, {
          params: {
            store_id: storeId.value,
            agent_id: agentId.value
          }
        })
        if (res.data.code === 200) {
          const data = res.data.data
          Object.assign(form, data)
          Cookies.set('user_info', JSON.stringify(data), { expires: 7 })
        } else {
          ElMessage.error(res.data.message || '获取失败')
        }
      } catch (err) {
        console.error(err)
        ElMessage.error('获取个人信息失败')
      } finally {
        loading.close()
      }
    }

    const saveBaseInfo = async () => {
      const valid = await new Promise(resolve => {
        formRef.value?.validate(valid => resolve(valid))
      })
      if (!valid) return

      submitting.value = true
      const loading = ElLoading.service({ text: '保存中...' })

      try {
        const payload = {
          action: 'update_profile',
          store_id: storeId.value,
          agent_id: agentId.value,
          nickName: form.nickName,
          mobile: form.mobile
        }
        const res = await axios.post(`${config.BASE_URL}/shop/api/profile_api.php`, payload)

        if (res.data.code === 200) {
          ElMessage.success('基本信息保存成功！')
          await fetchProfile()
        } else {
          ElMessage.error(res.data.message || '保存失败')
        }
      } catch (err) {
        ElMessage.error('保存失败')
      } finally {
        submitting.value = false
        loading.close()
      }
    }

    const formatCreateTime = (timestamp) => {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    }

    onMounted(() => {
      fetchProfile()
    })

    return {
      form,
      formRules,
      formRef,
      submitting,
      saveBaseInfo,
      formatCreateTime
    }
  }
}
</script>

<style scoped lang="scss">
.profile-page {
  padding: 0;
  width: 100%;
}

.page-header {
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
  }

  .page-desc {
    font-size: 14px;
    color: #8c8c8c;
  }
}

.profile-container {
  width: 100%;
  max-width: 800px;
}

.profile-card {
  border-radius: 16px;
  overflow: hidden;
}

.card-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
}

.profile-form {
  margin-top: 12px;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  line-height: 1.4;
}

.save-btn-item {
  margin-top: 16px;
  display: flex;
  align-items: center;
}

.save-btn {
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 500;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
}
</style>